import React from 'react';
import { CommitmentMetrics as CommitmentMetricsType, ProviderCommitmentData } from '../../types/dashboard';
import { PROVIDER_COLORS, PROVIDER_PALETTES, ProviderColorKey } from '../../utils/providerColors';

/**
 * CommitmentMetrics component - Provider-specific commitment breakdown
 *
 * REQ-012-HYBRID: Shows AWS Savings Plans, Azure Reservations, and GCP CUDs
 * separately with provider brand colors. Each provider section displays
 * coverage % and utilization % when data is available, or a "Coming Soon"
 * state when commitment data is not yet integrated.
 *
 * Previously displayed hardcoded values (72.5% coverage, 87.3% utilization)
 * which were removed in Task 20. Now shows a proper provider breakdown.
 */

interface CommitmentMetricsProps {
  metrics?: CommitmentMetricsType;
}

/** Provider display configuration */
interface ProviderConfig {
  key: ProviderColorKey;
  label: string;
  shortLabel: string;
  icon: React.ReactNode;
}

const PROVIDER_CONFIGS: ProviderConfig[] = [
  {
    key: 'aws',
    label: 'AWS Savings Plans',
    shortLabel: 'AWS',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M6.763 10.036c0 .296.032.535.088.71.064.176.144.368.256.576.04.063.056.127.056.183 0 .08-.048.16-.152.24l-.503.335a.383.383 0 01-.208.072c-.08 0-.16-.04-.239-.112a2.47 2.47 0 01-.287-.375 6.18 6.18 0 01-.248-.471c-.622.734-1.405 1.101-2.347 1.101-.67 0-1.205-.191-1.596-.574-.391-.384-.59-.894-.59-1.533 0-.678.239-1.23.726-1.644.487-.415 1.133-.623 1.955-.623.272 0 .551.024.846.064.296.04.6.104.918.176v-.583c0-.607-.127-1.03-.375-1.277-.255-.248-.686-.367-1.3-.367-.28 0-.568.032-.863.104-.296.072-.583.16-.863.272a2.287 2.287 0 01-.28.104.488.488 0 01-.127.023c-.112 0-.168-.08-.168-.247v-.391c0-.128.016-.224.056-.28a.597.597 0 01.224-.167c.28-.144.616-.264 1.01-.36a4.84 4.84 0 011.244-.152c.95 0 1.644.216 2.091.647.44.43.662 1.085.662 1.963v2.586zm-3.24 1.214c.263 0 .535-.048.822-.144.287-.096.543-.271.758-.51.128-.152.224-.32.272-.512.048-.191.08-.423.08-.694v-.335a6.66 6.66 0 00-.735-.136 6.02 6.02 0 00-.75-.048c-.535 0-.926.104-1.19.32-.263.215-.39.518-.39.917 0 .375.095.655.295.846.191.2.47.296.838.296zm6.41.862c-.144 0-.24-.024-.304-.08-.064-.048-.12-.16-.168-.311L7.586 5.55a1.398 1.398 0 01-.072-.32c0-.128.064-.2.191-.2h.783c.151 0 .255.025.31.08.065.048.113.16.16.312l1.342 5.284 1.245-5.284c.04-.16.088-.264.151-.312a.549.549 0 01.32-.08h.638c.152 0 .256.025.32.08.063.048.12.16.151.312l1.261 5.348 1.381-5.348c.048-.16.104-.264.16-.312a.52.52 0 01.311-.08h.743c.128 0 .2.064.2.2 0 .04-.009.08-.017.128a1.137 1.137 0 01-.056.2l-1.923 6.17c-.048.16-.104.264-.168.312a.549.549 0 01-.303.08h-.687c-.151 0-.255-.024-.32-.08-.063-.056-.119-.16-.15-.32l-1.238-5.148-1.23 5.14c-.04.16-.087.264-.15.32-.065.056-.177.08-.32.08zm10.256.215c-.415 0-.83-.048-1.229-.143-.399-.096-.71-.2-.918-.32-.128-.071-.215-.151-.247-.223a.563.563 0 01-.048-.224v-.407c0-.167.064-.247.183-.247.048 0 .096.008.144.024.048.016.12.048.2.08.271.12.566.215.878.279.319.064.63.096.95.096.502 0 .894-.088 1.165-.264a.86.86 0 00.415-.758.777.777 0 00-.215-.559c-.144-.151-.415-.287-.806-.415l-1.157-.36c-.583-.183-1.014-.454-1.277-.813a1.902 1.902 0 01-.4-1.158c0-.335.073-.63.216-.886.144-.255.335-.479.575-.654.24-.184.51-.32.83-.415a3.57 3.57 0 011.013-.144c.183 0 .375.008.559.032.191.024.367.056.543.096.168.04.327.08.479.128.151.048.271.096.367.144a.724.724 0 01.263.2.439.439 0 01.072.263v.375c0 .168-.064.256-.184.256a.83.83 0 01-.303-.096 3.652 3.652 0 00-1.532-.311c-.455 0-.815.071-1.062.223-.248.152-.375.383-.375.694 0 .224.08.415.24.567.16.152.454.303.87.44l1.133.358c.574.184.99.44 1.237.767.248.327.367.702.367 1.117 0 .343-.072.655-.207.926-.144.272-.336.511-.583.703-.248.2-.543.343-.886.447-.36.111-.734.167-1.142.167z" />
      </svg>
    ),
  },
  {
    key: 'azure',
    label: 'Azure Reservations',
    shortLabel: 'Azure',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M5.483 21.3H14.3l4.818-8.1-5.35-6.3L5.483 21.3zm8.4-17.7l-5.1 6.6h6.6l-3.9 11.1L19.2 12H12.6l5.1-8.4h-3.817z" />
      </svg>
    ),
  },
  {
    key: 'gcp',
    label: 'GCP Committed Use Discounts',
    shortLabel: 'GCP',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12.19 2.38a9.344 9.344 0 00-9.234 6.893c.053-.02-.055.013 0 0-3.875 2.551-3.922 8.111-.247 10.941l.006-.007-.007.003a6.542 6.542 0 005.181 2.584h8.347a6.544 6.544 0 006.344-4.907 6.537 6.537 0 00-1.895-6.281A9.34 9.34 0 0012.19 2.38zm-.358 4.146c1.244-.04 2.518.368 3.537 1.202a5.2 5.2 0 011.63 2.376l-2.093 1.209a2.847 2.847 0 00-3.074-1.883 2.853 2.853 0 00-2.248 2.262l-2.092 1.209a5.159 5.159 0 01.602-4.185 5.207 5.207 0 013.738-2.19zm-5.59 5.63l2.092-1.21a2.852 2.852 0 002.341 2.932 2.851 2.851 0 002.98-1.313l2.093 1.209a5.159 5.159 0 01-3.168 2.64 5.207 5.207 0 01-4.426-.86l-.006.003a4.193 4.193 0 01-1.906-3.4z" />
      </svg>
    ),
  },
];

export const CommitmentMetrics: React.FC<CommitmentMetricsProps> = ({ metrics }) => {
  const providerBreakdown = metrics?.providerBreakdown;

  return (
    <div className="rounded-lg border border-border bg-card p-6 shadow-sm dark:border-border dark:bg-card">
      <h3 className="mb-4 text-lg font-semibold text-foreground">
        Commitment Coverage & Utilization
      </h3>

      {/* Provider-specific breakdown grid */}
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-3"
        role="list"
        aria-label="Provider commitment breakdown"
      >
        {PROVIDER_CONFIGS.map((config) => {
          const data = providerBreakdown?.[config.key];
          return (
            <ProviderCommitmentCard
              key={config.key}
              config={config}
              data={data}
            />
          );
        })}
      </div>
    </div>
  );
};

/** Individual provider commitment card */
interface ProviderCommitmentCardProps {
  config: ProviderConfig;
  data?: ProviderCommitmentData;
}

const ProviderCommitmentCard: React.FC<ProviderCommitmentCardProps> = ({ config, data }) => {
  const palette = PROVIDER_PALETTES[config.key];
  const color = PROVIDER_COLORS[config.key];
  const hasData = data?.dataAvailable === true;

  return (
    <div
      className="rounded-lg border border-border p-4 dark:border-border"
      role="listitem"
      aria-label={`${config.label} commitment metrics`}
    >
      {/* Provider header with color accent */}
      <div className="mb-3 flex items-center gap-2">
        <div
          className="flex h-8 w-8 items-center justify-center rounded-md"
          style={{ backgroundColor: `${color}15` }}
        >
          <span style={{ color }}>{config.icon}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">
            {config.shortLabel}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {data?.commitmentType || config.label.replace(`${config.shortLabel} `, '')}
          </p>
        </div>
      </div>

      {hasData ? (
        /* Illustrative metrics display - no real Reserved Instance/Savings
           Plan/CUD data source is integrated for any tenant yet, so this is
           always a server-side estimate, not measured commitment usage. */
        <div className="space-y-3">
          <p className="text-xs italic text-muted-foreground/70">Illustrative estimate</p>
          {/* Coverage metric */}
          <MetricBar
            label="Coverage"
            value={data!.coveragePercentage}
            color={color}
            fillClass={palette.fillClass}
          />

          {/* Utilization metric */}
          <MetricBar
            label="Utilization"
            value={data!.utilizationPercentage}
            color={color}
            fillClass={palette.fillClass}
          />
        </div>
      ) : (
        /* Coming Soon / No Data state */
        <div className="flex flex-col items-center py-3 text-center">
          <div
            className="mb-2 flex h-10 w-10 items-center justify-center rounded-full"
            style={{ backgroundColor: `${color}10` }}
          >
            <svg
              className="h-5 w-5"
              style={{ color }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-xs font-medium text-muted-foreground">
            Coming Soon
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            No data available
          </p>
        </div>
      )}
    </div>
  );
};

/** Metric progress bar component */
interface MetricBarProps {
  label: string;
  value: number;
  color: string;
  fillClass: string;
}

const MetricBar: React.FC<MetricBarProps> = ({ label, value, color, fillClass }) => {
  const clampedValue = Math.min(100, Math.max(0, value));

  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span
          className="font-semibold"
          style={{ color }}
          aria-label={`${label}: ${clampedValue.toFixed(1)} percent`}
        >
          {clampedValue.toFixed(1)}%
        </span>
      </div>
      <div
        className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary"
        role="progressbar"
        aria-valuenow={clampedValue}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label} progress`}
      >
        <div
          className={`h-full rounded-full transition-all duration-300 ${fillClass}`}
          style={{ width: `${clampedValue}%` }}
        />
      </div>
    </div>
  );
};
